import React, { useState, useEffect, useCallback } from 'react';
import { getPackages, deletePackage } from '../../services/packagesService';
// REMOVED: Global list imports - Migrated to dedicated page
// import { getGlobalInclusions, getGlobalExclusions, createGlobalInclusion, createGlobalExclusion, deleteGlobalInclusion, deleteGlobalExclusion } from '../../services/globalListsService';
import Loader from '../../components/common/Loader';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import { Link, useNavigate } from 'react-router-dom';
import { getDestinations, getSubDestinations } from '../../services/locationsService';
import { PlusCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
// REMOVED: PlusIcon, TrashIcon - No longer needed for inclusion/exclusion management

// REMOVED: Debounce hook - Using controlled search instead
// function useDebounce(value, delay) {
//   const [debouncedValue, setDebouncedValue] = useState(value);
//   useEffect(() => {
//     const handler = setTimeout(() => {
//       setDebouncedValue(value);
//     }, delay);
//     return () => {
//       clearTimeout(handler);
//     };
//   }, [value, delay]);
//   return debouncedValue;
// }

function PackageListPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [allSubDestinations, setAllSubDestinations] = useState([]);
  const [filters, setFilters] = useState({
    destinationId: '',
    subDestinationId: '',
    status: '',
    nights: '',
    days: '',
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  
  // Search input state (separate from filters to prevent immediate API calls)
  const [searchInput, setSearchInput] = useState('');
  
  // Flag to prevent useEffect from triggering during manual operations
  const [isManualOperation, setIsManualOperation] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 100,
    total: 0,
    pages: 0
  });

  // 🚀 PERFORMANCE OPTIMIZATION: Cache destinations and sub-destinations
  const [destinationsCache, setDestinationsCache] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [lastLocationsFetch, setLastLocationsFetch] = useState(0);
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes cache
  
  // Dynamic inclusion/exclusion management - REMOVED: Migrated to dedicated page
  // const [inclusions, setInclusions] = useState([]);
  // const [exclusions, setExclusions] = useState([]);
  // const [newInclusion, setNewInclusion] = useState('');
  // const [newExclusion, setNewExclusion] = useState('');
  // const [showManagement, setShowManagement] = useState(false);
  
  const navigate = useNavigate();

  // Remove debounce - we'll use controlled search instead
  // const debouncedSearchTerm = useDebounce(filters.search, 500);

  // 🚀 PERFORMANCE OPTIMIZATION: Load destinations/sub-destinations only once
  useEffect(() => {
    loadDestinationsIfNeeded();
  }, []); // Only run once on mount

  // Effect for package data only (excluding locations)
  useEffect(() => {
    if (destinationsCache && !isManualOperation) { // Skip if manual operation is in progress
      // Add small delay to prevent race conditions during rapid state changes
      const timeoutId = setTimeout(() => {
        fetchPackagesOnly();
      }, 50); // Slightly longer delay for packages page due to more complex filters
      
      return () => clearTimeout(timeoutId);
    }
  }, [
    filters.destinationId, 
    filters.subDestinationId, 
    filters.status, 
    filters.nights, 
    filters.days,
    filters.search, // Direct search filter, not debounced
    pagination.page,
    pagination.per_page,
    destinationsCache, // Include cache as dependency
    isManualOperation // Include flag as dependency
  ]);

  // Remove separate debounced search effect - now handled in main effect
  // useEffect(() => {
  //   if (debouncedSearchTerm !== filters.search) {
  //     return;
  //   }
  //   if (destinationsCache) {
  //     fetchPackagesOnly();
  //   }
  // }, [debouncedSearchTerm, destinationsCache]);

  // 🚀 OPTIMIZED: Load destinations/sub-destinations with caching
  const loadDestinationsIfNeeded = async () => {
    const now = Date.now();
    
    // Check localStorage cache first (persists across page refreshes)
    const localStorageKey = 'tourkingdom_destinations_cache';
    const storedCache = localStorage.getItem(localStorageKey);
    
    if (storedCache) {
      try {
        const parsedCache = JSON.parse(storedCache);
        if (parsedCache && (now - parsedCache.timestamp) < CACHE_DURATION) {
          setDestinationsCache(parsedCache.data);
          setDestinations(parsedCache.data.destinations);
          setAllSubDestinations(parsedCache.data.subDestinations);
          setLastLocationsFetch(parsedCache.timestamp);
          return;
        } else {
          localStorage.removeItem(localStorageKey);
        }
      } catch (e) {
        localStorage.removeItem(localStorageKey);
      }
    }
    
    // Check if in-memory cache is valid (not expired)
    if (destinationsCache && (now - lastLocationsFetch) < CACHE_DURATION) {
      setDestinations(destinationsCache.destinations);
      setAllSubDestinations(destinationsCache.subDestinations);
      return;
    }

    setLocationsLoading(true);
    try {
      // Fetch both in parallel
      const [destData, subDestData] = await Promise.all([
        getDestinations(),
        getSubDestinations({}, 1, 100) // Get up to 100 sub-destinations
      ]);

      // Cache the results with multiple fallbacks
      const cache = {
        destinations: destData?.data || destData?.destinations || destData || [],
        subDestinations: subDestData?.data || subDestData?.subDestinations || subDestData || []
      };
      
      // Store in both memory and localStorage
      setDestinationsCache(cache);
      setDestinations(cache.destinations);
      setAllSubDestinations(cache.subDestinations);
      setLastLocationsFetch(now);
      
      // Store in localStorage for persistence across refreshes
      const cacheWithTimestamp = {
        data: cache,
        timestamp: now
      };
      localStorage.setItem(localStorageKey, JSON.stringify(cacheWithTimestamp));
    } catch (err) {
      setError('Failed to load destinations: ' + err.message);
    } finally {
      setLocationsLoading(false);
    }
  };

  // Force refresh destinations cache
  const refreshDestinationsCache = async () => {
    // Clear both memory and localStorage cache
    setLastLocationsFetch(0);
    localStorage.removeItem('tourkingdom_destinations_cache');
    await loadDestinationsIfNeeded();
  };

  // Debug function to check authentication
  const checkAuthentication = () => {
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const userData = localStorage.getItem('user_data');
    
    console.log('🔐 Authentication Status:', {
      hasAccessToken: !!token,
      hasRefreshToken: !!refreshToken,
      hasUserData: !!userData,
      tokenLength: token ? token.length : 0,
      apiUrl: process.env.REACT_APP_API_URL || 'https://api-tk.up.railway.app'
    });
    
    if (!token) {
      alert('No authentication token found. Please log in again.');
      return false;
    }
    
    return true;
  };

  // Fetch only packages data
  const fetchPackagesOnly = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Check authentication before API call
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      // Prepare API filters - map frontend filter names to backend parameter names
      const apiFilters = {};
      if (filters.search) apiFilters.search = filters.search;
      if (filters.status) apiFilters.status = filters.status;
      if (filters.destinationId) apiFilters.destination_id = filters.destinationId;
      if (filters.subDestinationId) apiFilters.sub_destination_id = filters.subDestinationId;
      if (filters.nights) apiFilters.nights = parseInt(filters.nights);
      if (filters.days) apiFilters.days = parseInt(filters.days);
      if (filters.sort_by) apiFilters.sort_by = filters.sort_by;
      if (filters.sort_order) apiFilters.sort_order = filters.sort_order;

      // Only fetch packages, not destinations!
      const packageResponse = await getPackages(apiFilters, pagination.page, pagination.per_page);

      // Update state with package data only
      if (packageResponse && packageResponse.success !== false) {
        setPackages(packageResponse.data || []);
        if (packageResponse.pagination) {
          setPagination(packageResponse.pagination);
        }
      } else {
        setPackages([]);
      }

    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Unknown error occurred';
      
      // Handle specific 403 Forbidden error
      if (err.message && err.message.includes('403')) {
        setError('Access denied. Please check your permissions or contact administrator.');
      } else if (err.message && err.message.includes('401')) {
        setError('Session expired. Please log in again.');
        // Optionally redirect to login
        // window.location.href = '/login';
      } else {
        setError(`Failed to load packages: ${errorMessage}`);
      }
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      setLoading(true);
      setError('');
      try {
        await deletePackage(id);
        await fetchPackagesOnly(); // Only refetch packages, not destinations
        alert('Package deleted successfully!');
      } catch (err) {
        setError('Failed to delete package: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // Handle search input separately (don't trigger API calls)
    if (name === 'search') {
      setSearchInput(value);
      return;
    }
    
    setFilters(prev => {
      const newFilters = { ...prev, [name]: value };
      if (name === 'destinationId') {
        newFilters.subDestinationId = '';
      }
      return newFilters;
    });
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Perform actual search when Enter is pressed or search button is clicked
  const performSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle Enter key press for search
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  // Clear search
  const clearSearch = async () => {
    // Set flag to prevent useEffect from triggering
    setIsManualOperation(true);
    
    // Clear all search states first
    setSearchInput('');
    setFilters(prev => ({ ...prev, search: '' }));
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Manually fetch packages with empty search to avoid double API calls
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      // Prepare API filters without search
      const apiFilters = {};
      if (filters.status) apiFilters.status = filters.status;
      if (filters.destinationId) apiFilters.destination_id = filters.destinationId;
      if (filters.subDestinationId) apiFilters.sub_destination_id = filters.subDestinationId;
      if (filters.nights) apiFilters.nights = parseInt(filters.nights);
      if (filters.days) apiFilters.days = parseInt(filters.days);
      if (filters.sort_by) apiFilters.sort_by = filters.sort_by;
      if (filters.sort_order) apiFilters.sort_order = filters.sort_order;

      const packageResponse = await getPackages(apiFilters, 1, pagination.per_page);

      if (packageResponse && packageResponse.success !== false) {
        setPackages(packageResponse.data || []);
        if (packageResponse.pagination) {
          setPagination(prev => ({
            ...prev,
            ...packageResponse.pagination,
            page: 1
          }));
        }
      } else {
        setPackages([]);
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Unknown error occurred';
      setError(`Failed to load packages: ${errorMessage}`);
      setPackages([]);
    } finally {
      // Reset flag to allow useEffect to work normally
      setTimeout(() => setIsManualOperation(false), 100);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getFilteredSubDestinations = () => {
    if (!filters.destinationId) {
      return []; // Return empty array when no destination is selected
    }
    
    // � DEBUG: Log input data for troubleshooting
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Sub-destination filtering input:', {
        selectedDestinationId: filters.destinationId,
        selectedDestinationType: typeof filters.destinationId,
        totalSubDestinations: allSubDestinations.length,
        sampleSubDestinations: allSubDestinations.slice(0, 3).map(sub => ({
          id: sub.id,
          name: sub.name,
          destination_id: sub.destination_id,
          destination_id_type: typeof sub.destination_id
        }))
      });
    }
    
    // Try both loose and strict comparison
    const filtered = allSubDestinations.filter(sub => {
      // First try direct comparison (for same types)
      if (sub.destination_id == filters.destinationId) {
        return true;
      }
      // Then try number comparison (convert both to numbers)
      const subDestId = parseInt(sub.destination_id);
      const filterDestId = parseInt(filters.destinationId);
      return !isNaN(subDestId) && !isNaN(filterDestId) && subDestId === filterDestId;
    });
    
    return filtered;
  };

  // REMOVED: Inclusion/Exclusion management functions - Migrated to dedicated page
  // const addInclusion = async () => { ... }
  // const removeInclusion = async (inclusion) => { ... }
  // const addExclusion = async () => { ... }
  // const removeExclusion = async (exclusion) => { ... }

  const headers = ['Title', 'N/D', 'Status', 'Locations', 'Featured', 'Actions'];

  const renderRow = (pkg) => {
    // Helper function to safely parse JSONB arrays
    const parseJsonbArray = (data) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }
      return [];
    };

    // Parse destination and sub-destination IDs
    const destinationIds = parseJsonbArray(pkg.destination_ids);
    const subDestinationIds = parseJsonbArray(pkg.sub_destination_ids);

    return (
      <tr key={pkg.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200">
        <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs">
          <div className="flex items-center">
            <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mr-3"></div>
            <span className="truncate">{pkg.title}</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">{pkg.nights}N/{pkg.days}D</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${
            pkg.status === 'published' ? 'bg-green-100 text-green-800 border-green-200' : 
            pkg.status === 'draft' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 
            'bg-gray-100 text-gray-800 border-gray-200'
          }`}>
            {pkg.status}
          </span>
        </td>
        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
          <div className="flex flex-wrap gap-1">
            {/* Render destinations */}
            {destinationIds.slice(0, 2).map((destId, index) => {
              const dest = destinations.find(d => d.id === destId);
              if (!dest) return null;

              return (
                <span key={`dest-${destId}-${index}`} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full border border-blue-200">
                  {dest.name.length > 10 ? dest.name.substring(0, 10) + '...' : dest.name}
                </span>
              );
            })}
            
            {/* Render sub-destinations */}
            {subDestinationIds.slice(0, 2 - destinationIds.length).map((subDestId, index) => {
              const subDest = allSubDestinations.find(sd => sd.id === subDestId);
              if (!subDest) return null;

              return (
                <span key={`sub-${subDestId}-${index}`} className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full border border-green-200">
                  {subDest.name.length > 10 ? subDest.name.substring(0, 10) + '...' : subDest.name}
                </span>
              );
            })}
            
            {/* Show "more" indicator if there are additional locations */}
            {(destinationIds.length + subDestinationIds.length) > 2 && (
              <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full border border-gray-200">
                +{(destinationIds.length + subDestinationIds.length) - 2} more
              </span>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {pkg.is_featured && <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-800 border border-purple-200">Featured</span>}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
          <Link to={`/packages/edit/${pkg.id}`} className="text-indigo-600 hover:text-indigo-800 px-3 py-1 rounded-lg hover:bg-indigo-50 transition-colors duration-200 text-sm font-medium">Edit</Link>
          <button onClick={() => handleDelete(pkg.id)} className="text-red-600 hover:text-red-800 px-3 py-1 rounded-lg hover:bg-red-50 transition-colors duration-200 text-sm font-medium">Delete</button>
        </td>
      </tr>
    );
  };

  if (loading || locationsLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl shadow-lg" role="alert">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
            <span className="block sm:inline font-medium">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
          Packages & Blogs
        </h2>
        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            onClick={refreshDestinationsCache}
            className="inline-flex items-center py-2 px-4 text-sm font-medium rounded-lg border-2 border-green-500 text-green-600 hover:bg-green-50 hover:border-green-600 transition-all duration-200"
            title="Refresh destinations and filter data to get latest updates"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" /> Refresh Data
          </Button>
          
          {/* Debug button for checking authentication */}
          {process.env.NODE_ENV === 'development' && (
            <Button 
              variant="outline" 
              onClick={checkAuthentication}
              className="inline-flex items-center py-2 px-4 text-sm font-medium rounded-lg border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50 hover:border-yellow-600 transition-all duration-200"
              title="Check authentication status"
            >
              🔐 Check Auth
            </Button>
          )}
          
          <Link to="/packages/new">
            <Button variant="primary" className="inline-flex items-center py-2 px-4 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200">
              <PlusCircleIcon className="h-4 w-4 mr-2" /> Add New Package
            </Button>
          </Link>
        </div>
      </div>

      {/* REMOVED: Inclusion/Exclusion Management Section - Migrated to dedicated page /pkg-inclusion-exclusion */}

      {/* Error Display with Enhanced Debug Info */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center mb-2">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
            <span className="font-medium text-red-800">Error Loading Packages</span>
          </div>
          <p className="text-red-700 mb-3">{error}</p>
          
          {/* Debug Info for 403 errors */}
          {error.includes('403') && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium mb-2">Debug Information:</p>
              <div className="text-xs text-yellow-700 space-y-1">
                <div>Auth Token: {localStorage.getItem('access_token') ? '✅ Present' : '❌ Missing'}</div>
                <div>API URL: {process.env.REACT_APP_API_URL || 'https://api-tk.up.railway.app'}</div>
                <div>Applied Filters: {JSON.stringify(filters, null, 2)}</div>
                <div className="mt-2">
                  <button 
                    onClick={() => window.location.href = '/login'}
                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
            Search & Filters
          </h3>
          <Button 
            variant="outline" 
            onClick={refreshDestinationsCache}
            className="inline-flex items-center py-2 px-4 text-sm font-medium rounded-lg border-2 border-blue-500 text-blue-600 hover:bg-blue-50 transition-all duration-200"
            title="Refresh destinations and sub-destinations data"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" /> Refresh Filter Data
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="flex">
            <input
              type="text"
              name="search"
              value={searchInput}
              onChange={handleFilterChange}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search packages... (Press Enter to search)"
              className="flex-1 py-3 px-4 border border-gray-300 bg-white rounded-l-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200"
            />
            <button
              onClick={performSearch}
              className="px-4 py-3 bg-indigo-500 text-white rounded-r-lg hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-200 text-sm"
            >
              Search
            </button>
          </div>
          
          {/* Show current search and clear option */}
          {filters.search && (
            <div className="xl:col-span-6 flex items-center justify-between text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
              <span>Searching for: "{filters.search}"</span>
              <button
                onClick={clearSearch}
                className="text-red-500 hover:text-red-700 underline ml-4"
              >
                Clear Search
              </button>
            </div>
          )}
          
          <select
            name="destinationId"
            value={filters.destinationId}
            onChange={handleFilterChange}
            className="block w-full py-3 px-4 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200"
          >
            <option value="">All Destinations</option>
            {destinations.map(dest => (
              <option key={dest.id} value={dest.id}>{dest.name}</option>
            ))}
          </select>

          <select
            name="subDestinationId"
            value={filters.subDestinationId}
            onChange={handleFilterChange}
            className="block w-full py-3 px-4 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200"
            disabled={!filters.destinationId}
          >
            <option value="">
              {!filters.destinationId 
                ? 'Select destination first' 
                : getFilteredSubDestinations().length === 0 
                  ? 'No sub-destinations available'
                  : 'All Sub-destinations'}
            </option>
            {getFilteredSubDestinations().map(sub => (
              <option key={sub.id} value={sub.id}>{sub.name}</option>
            ))}
          </select>

          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="block w-full py-3 px-4 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>

          <select
            name="nights"
            value={filters.nights}
            onChange={handleFilterChange}
            className="block w-full py-3 px-4 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200"
          >
            <option value="">All Nights</option>
            {[...Array(15)].map((_, i) => (
              <option key={i} value={i + 1}>{i + 1} Night{i > 0 ? 's' : ''}</option>
            ))}
          </select>

          <select
            name="days"
            value={filters.days}
            onChange={handleFilterChange}
            className="block w-full py-3 px-4 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200"
          >
            <option value="">All Days</option>
            {[...Array(15)].map((_, i) => (
              <option key={i} value={i + 1}>{i + 1} Day{i > 0 ? 's' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
        <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
            Packages List
          </h3>
        </div>
        <Table headers={headers} data={packages} renderRow={renderRow} />
      </div>

      {/* Pagination Controls */}
      {pagination.pages > 1 && (
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 flex items-center">
              <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} packages
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                Previous
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const pageNum = Math.max(1, pagination.page - 2) + i;
                if (pageNum > pagination.pages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 text-sm border rounded-lg font-medium transition-all duration-200 ${
                      pageNum === pagination.page
                        ? 'bg-indigo-500 text-white border-indigo-500 shadow-md'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PackageListPage;